// @flow
import { useDispatch } from 'react-redux';

import type { Currency } from '@gandi/billing';
import { organizationsResource } from '@gandi/redux-organization';
import { sharingResource, fetchOneOrganization } from '@gandi/redux-sharing';

import { useUpdateCartProductsOwnership } from '../../modules/cartOwnership';
import type { Contact, OrganizationFormData, Role } from '../types';

import { useCurrencySync } from './useCurrencySync';
import { useExistingAccountAction } from './useExistingAccountAction';
import { useFirstBuyNavigation } from './useFirstBuyNavigation';
import type { Response } from './useHandleFormError';

export function useHandleFormSuccess({
  firstBuy,
  chooseProductOwner,
  type,
  role,
  currency,
  currencyState,
  contact,
  setCurrentRadioValue,
  onSuccess,
  studio,
}: {
  firstBuy?: boolean,
  chooseProductOwner: boolean,
  type: OrganizationFormData['type'],
  role?: Role,
  currency?: Currency,
  currencyState?: Currency,
  contact?: Contact,
  setCurrentRadioValue?: React.ReactSetStateFunction<?string>,
  studio?: boolean,
  onSuccess?: () => void,
}): (resp: Response) => Promise<void> {
  const dispatch = useDispatch();

  const updateCartProductsOwnership = useUpdateCartProductsOwnership();

  const { syncCurrency } = useCurrencySync({ currency, currencyState });
  const navigateFirstBuy = useFirstBuyNavigation({
    chooseProductOwner,
    type,
  });
  const { existingAccountAction } = useExistingAccountAction({
    role,
    setCurrentRadioValue,
  });

  return async (resp: Response) => {
    // resp will return id for POST
    // else for PATCH we should use the contact.id
    const id = resp.data.id || contact?.id;

    if (!id)
      throw new Error('No `id` returns by the POST orga. Or no `contact.id`');

    await Promise.all([
      dispatch(organizationsResource.invalidate()),
      dispatch(sharingResource.invalidate()),
    ]);

    // To update redux-sharing
    await dispatch(fetchOneOrganization(id));
    // no id and update the currency will fails
    // customers do not have currency
    if (role !== 'CUSTOMER') await syncCurrency(id);

    if (firstBuy || studio) {
      if (studio) {
        await dispatch(
          updateCartProductsOwnership({
            filter: [{ productType: 'paas', process: 'renew' }],
          }),
        );
      }
      await navigateFirstBuy(id);
    } else {
      if (typeof onSuccess === 'function') onSuccess();
      // $FlowFixMe[incompatible-call]
      await existingAccountAction(resp, id);
    }
  };
}
